import { DeepPartial } from '@/interfaces/interfaces';
import { IPlaceholderField } from '@/interfaces/placeholder-field';
import { COLUMN_TYPES } from './column-types';
import { DEFAULT_LINE_ITEM_HEADER_COLOR } from './line-items';
import {
  EXTENDED_MONDAY_COLUMN_TYPE
} from './monday-column-type.constants';
import { DEFAULT_DATE_FORMAT } from './date-formats';

export const LOCAL_STORAGE_KEYS = {
  SESSION_TOKEN: 'sessionToken',
  APP_VERSION: 'appVersion',
};

export const API_URL_V1 =
  import.meta.env.VITE_GETSIGN_BASE_API_URL + '/api/v1/';
export const API_URL_V2 =
  import.meta.env.VITE_GETSIGN_BASE_API_URL + '/api/v2/';
export const API_URL_V3 =
  import.meta.env.VITE_GETSIGN_BASE_API_URL + '/api/v3/';

export const IS_DEV = import.meta.env.VITE_IS_DEV === 'true';

export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;

export const CLARITY_TAG_NAME = {
  PAGE_NAME: 'app',
  FILE_UPLOAD_COMPONENT: 'file-upload-component',
};

export const CLARITY_TAG_VALUE = {
  PAGE_NAME: {
    PDF_EDITOR: 'pdf-editor',
    PDF_TEMPLATE_EDITOR: 'pdf-template-editor',
    PDF_TEMPLATE_GALLERY_EDITOR: 'pdf-template-gallery-editor',
    PDF_PREVIEW: 'pdf-preview',
    PDF_SIGN: 'pdf-sign',
    WORD_EDITOR: 'word-editor',
  },
  FILE_UPLOAD_COMPONENT: 'true',
};

export const SIGNATURE_PAD_FIELD = {
  INITIALS: 'initials-field',
  SIGNATURE: 'signature-field',
};

export const STANDARD_FIELD_WITH_SIGNER = {
  CHECKBOX: 'checkbox',
  DROPDOWN: 'sd-dropdown',
  PAYMENT: 'sd-payment',
  DATE_PICKER: 'sd-datepicker',
  TEXT_BOX: 'text-box',
  FILE: 'sd-file',
  SIGN_DATE: 'sign-date',
};

/** Input type for text box placeholders (format hint + validation). Optional; distinct from field type (e.g. text-box). */
export const TEXT_BOX_INPUT_TYPE = {
  TEXT: 'text',
  EMAIL: 'email',
  PHONE: 'phone',
  NUMBER: 'number',
} as const;

export type TextBoxInputType =
  (typeof TEXT_BOX_INPUT_TYPE)[keyof typeof TEXT_BOX_INPUT_TYPE];

export const STANDARD_FIELD_FOR_SIGNER_FORM = {
  CHECKBOX: 'checkbox',
  DROPDOWN: 'sd-dropdown',
  PAYMENT: 'sd-payment',
  DATE_PICKER: 'sd-datepicker',
  TEXT_BOX: 'text-box',
  FILE: 'sd-file',
};

export const STANDARD_FIELD_WITHOUT_SIGNER = {
  LINK: 'sd-link',
  IMAGE: 'sd-image',
  TEXT_AREA: 'sd-textarea',
};

export const FORM_FIELDS_WITH_SIGNER = Object.values({
  ...STANDARD_FIELD_WITH_SIGNER,
  ...SIGNATURE_PAD_FIELD,
});

export const FORM_FIELD = {
  ...SIGNATURE_PAD_FIELD,
  ...STANDARD_FIELD_WITH_SIGNER,
  ...STANDARD_FIELD_WITHOUT_SIGNER,
  LINE_ITEM: 'line-item',
};

export const SIGNATURE_FIELD_COLUMN_TYPES = [
  COLUMN_TYPES.EMAIL,
  COLUMN_TYPES.MIRROR_EMAIL_COLUMN,
  COLUMN_TYPES.PEOPLE,
];

export const SIGNER_SETTINGS_COLOR_CODES = [
  '#FEE2E2',
  '#E0F2FE',
  '#FFEDD5',
  '#CFFAFE',
  '#F3E8FF',
  '#E0E7FF',
  '#DCFCE7',
  '#ECFCCB',
  '#DFEDF2',
  '#EDE7D0',
  '#EDD0E0',
  '#E2CAB3',
  '#CCFBF1',
] as const;

export type SignerSettingsColorCode =
  (typeof SIGNER_SETTINGS_COLOR_CODES)[number];

export const STRIPE_ASSIGNEE_COLUMN_TYPES = [
  COLUMN_TYPES.EMAIL,
  COLUMN_TYPES.MIRROR_EMAIL_COLUMN,
  COLUMN_TYPES.PEOPLE,
];

export const SEARCH_PARAMS = {
  SESSION_TOKEN: 'sessionToken',
  APP_VERSION: 'version',
  IS_PREVIEW: 'isPreview',
  SHOULD_SAVE_TEMPLATE: 'tmpl',
  FROM_WORKFLOW: 'fromWorkflow',
};

export const OTP_LENGTH = 6;
export const BOARD_FIELD_TYPE = {
  NUMBERS: 'numbers',
  TEXT: 'text',
  DATE: 'date',
  EMAIL: 'email',
  PEOPLE: 'people',
  STATUS: 'status',
  FORMULA: 'formula',
  LINK: 'link',
  ITEM_NAME: 'item-name',
  FILE: 'file',
  LINE_ITEM: 'line-item',
};

export const DRAGGABLE_BUTTON_TYPE = {
  COLUMN: 'Column',
  CHIP: 'Chips',
};

export const DEFAULT_CHIP_COLOR = '#E6E6FA';
export const DEFAULT_FONT_SIZE = 10.6;
export const DEFAULT_LINE_HEIGHT = 1.25;
// Smallest a signature or standard field can be resized to. Kept small so
// fields fit the tiny boxes on real forms; AI detection uses the same floor
// (FIELD_MIN_DIMENSIONS in PDF-placeholder-detector) so detected fields are
// never rendered larger than their stored size.
export const FIELD_MIN_WIDTH = 20;
export const FIELD_MIN_HEIGHT = 10;
export const EDITOR_SIDEBAR_WIDTH = 288;
export const EDITOR_HEADER_HEIGHT = 66;

export const DEFAULT_CONFIG: Record<string, DeepPartial<IPlaceholderField>> = {
  SIGNATURE: {
    itemId: FORM_FIELD.SIGNATURE,
    title: 'Signature',
    type: FORM_FIELD.SIGNATURE,
    height: 30,
    width: 180,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    noPadding: true,
  },
  INITIALS: {
    itemId: FORM_FIELD.INITIALS,
    title: 'Initials',
    type: FORM_FIELD.INITIALS,
    height: 30,
    width: 180,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    noPadding: true,
  },
  CHECKBOX: {
    itemId: FORM_FIELD.CHECKBOX,
    title: 'Checkbox',
    type: FORM_FIELD.CHECKBOX,
    height: 25,
    width: 25,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    noPadding: true,
  },
  TEXT_BOX: {
    itemId: FORM_FIELD.TEXT_BOX,
    title: 'Text Box',
    type: FORM_FIELD.TEXT_BOX,
    height: 20,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    noPadding: true,
  },
  TEXT_AREA: {
    itemId: FORM_FIELD.TEXT_AREA,
    title: 'Text Area',
    type: FORM_FIELD.TEXT_AREA,
    height: 25,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
  },
  SIGN_DATE: {
    itemId: FORM_FIELD.SIGN_DATE,
    title: 'Sign Date',
    type: FORM_FIELD.SIGN_DATE,
    height: 16,
    width: 80,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    dateFormat: {
      format: DEFAULT_DATE_FORMAT,
      label: DEFAULT_DATE_FORMAT,
      value: 0,
    },
    requiresSigner: true,
  },
  DATE_PICKER: {
    itemId: FORM_FIELD.DATE_PICKER,
    title: 'Date Picker',
    type: FORM_FIELD.DATE_PICKER,
    height: 20,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    format: {
      format: DEFAULT_DATE_FORMAT,
      label: DEFAULT_DATE_FORMAT,
      value: 0,
    }
  },
  DROPDOWN: {
    itemId: FORM_FIELD.DROPDOWN,
    title: 'Dropdown',
    type: FORM_FIELD.DROPDOWN,
    height: 20,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    noPadding: true,
  },
  PAYMENT: {
    itemId: FORM_FIELD.PAYMENT,
    title: 'Make Payment',
    text: 'Make Payment',
    type: FORM_FIELD.PAYMENT,
    height: 24,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
  },
  LINK: {
    itemId: FORM_FIELD.LINK,
    type: FORM_FIELD.LINK,
    title: 'Link',
    height: 20,
    width: 150,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
      color: {
        r: 19,
        g: 130,
        b: 234,
      },
    },
  },
  IMAGE: {
    itemId: FORM_FIELD.IMAGE,
    type: FORM_FIELD.IMAGE,
    title: 'Image',
    height: 150,
    width: 200,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
  },
  FILE_UPLOAD: {
    itemId: FORM_FIELD.FILE,
    type: FORM_FIELD.FILE,
    title: 'File Upload',
    height: 90,
    width: 200,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
  },
  PREFILLED_IMAGE: {
    height: 150,
    width: 200,
  },
  PREFILLED_TEXT: {
    height: 16,
    width: 185,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
  },
  LINE_ITEM: {
    // for line items we set the height to 100% and width dynamically based on the pdf page width
    itemId: FORM_FIELD.LINE_ITEM,
    title: 'Sub Items',
    type: FORM_FIELD.LINE_ITEM,
    fontSize: DEFAULT_FONT_SIZE,
    fontStyle: {
      fontSize: DEFAULT_FONT_SIZE,
    },
    subItemSettings: {
      direction: 'ltr',
      sum: {
        checked: false,
        column: null,
        label: 'Sum',
      },
      header: {
        checked: false,
        color: DEFAULT_LINE_ITEM_HEADER_COLOR,
        rgb: { r: 245, g: 246, b: 248 },
        fontStyle: { bold: false, italics: false, fontSize: 14 },
      },
      body: {
        fontStyle: { bold: false, italics: false, fontSize: 12 },
      },
      tax: {
        checked: false,
        label: 'Tax',
        type: null,
      },
      currency: {
        checked: false,
        type: null,
        position: { value: 'before', label: 'Before the value' },
        column: [],
      },
      itemNameColumn: {
        checked: true,
        label: 'Item',
      },
    },
  },
};

export const SUPPORTED_COLUMNS_FROM_MONDAY: readonly string[] = [
  EXTENDED_MONDAY_COLUMN_TYPE.Date,
  EXTENDED_MONDAY_COLUMN_TYPE.LongText,
  EXTENDED_MONDAY_COLUMN_TYPE.Text,
  EXTENDED_MONDAY_COLUMN_TYPE.Location,
  EXTENDED_MONDAY_COLUMN_TYPE.Numbers,
  EXTENDED_MONDAY_COLUMN_TYPE.ItemId,
  EXTENDED_MONDAY_COLUMN_TYPE.Phone,
  EXTENDED_MONDAY_COLUMN_TYPE.Email,
  EXTENDED_MONDAY_COLUMN_TYPE.Mirror,
  EXTENDED_MONDAY_COLUMN_TYPE.Formula,
  EXTENDED_MONDAY_COLUMN_TYPE.Status,
  EXTENDED_MONDAY_COLUMN_TYPE.Dropdown,
  EXTENDED_MONDAY_COLUMN_TYPE.BoardRelation,
  EXTENDED_MONDAY_COLUMN_TYPE.File,
  EXTENDED_MONDAY_COLUMN_TYPE.Link,
  EXTENDED_MONDAY_COLUMN_TYPE.People,
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorEmailColumn,
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorNumericColumn,
  EXTENDED_MONDAY_COLUMN_TYPE.MirrorLinkColumn,
];


export const UPGRADE_URL = 'https://{slug}.monday.com/apps/installed_apps/10050849';

export const FEATURE_MESSAGES = {
  DEFAULT_MESSAGE: 'This feature isn\'t available with your current plan. Please upgrade to use this and other exciting features.',
}

export const DEFAULT_FONT_COLOR = { r: 0, g: 0, b: 0 };

// Backend error code returned (HTTP 422) when a PDF is encrypted/password-protected.
export const ENCRYPTED_PDF_ERROR_CODE = 'ENCRYPTED_PDF';

// Shared dialog wording for encrypted PDFs, used by both the client-side editor
// block and the backend-error handling on the preview surfaces.
export const ENCRYPTED_PDF_DIALOG = {
  title: 'Encrypted document',
  message:
    "This PDF is password-protected and can't be edited. Please upload an unprotected copy.",
};
